import React, { useState, useEffect } from 'react';
import { BomberosHeader } from '../../components/BomberosHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    bomberosActaAPI,
    bomberosCompaniaAPI,
    bomberosPersonalAPI,
    bomberosCapacitacionAPI,
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Skeleton } from '../../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Save,
    FileText,
    Clock,
    MapPin,
    Users,
    Calendar,
    CheckCircle,
    Loader2,
    Search,
} from 'lucide-react';

const TIPOS_ACTA = [
    { value: 'entrenamiento', label: 'Entrenamiento / Capacitación' },
    { value: 'guardia', label: 'Guardia de Compañía' },
    { value: 'academia', label: 'Academia' },
    { value: 'emergencia', label: 'Emergencia / Acto de Servicio' },
    { value: 'reunion', label: 'Reunión' },
    { value: 'inspeccion', label: 'Inspección de Equipos' },
    { value: 'otro', label: 'Otro' },
];

export default function ActaFormPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const isEditing = !!id;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [companias, setCompanias] = useState([]);
    const [bomberos, setBomberos] = useState([]);
    const [capacitaciones, setCapacitaciones] = useState([]);
    
    const [form, setForm] = useState({
        tipo: 'entrenamiento',
        titulo: '',
        descripcion: '',
        fecha: new Date().toISOString().split('T')[0],
        hora_inicio: '19:00',
        hora_fin: '21:00',
        lugar: '',
        compania_id: '',
        responsable_id: '',
        capacitacion_id: 'none',
        firma_responsable: false,
        observacion_general: '',
    });

    const [asistentes, setAsistentes] = useState([]);
    const [searchAsistente, setSearchAsistente] = useState('');

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [companiasRes, bomberosRes, capsRes] = await Promise.all([
                bomberosCompaniaAPI.getAll(),
                bomberosPersonalAPI.getAll({ activo: true }),
                bomberosCapacitacionAPI.getAll({ estado: 'en_curso' }),
            ]);
            
            setCompanias(companiasRes.data);
            setBomberos(bomberosRes.data);
            setCapacitaciones(capsRes.data);
            
            // Set default compania
            if (!isEditing && companiasRes.data.length > 0) {
                setForm(prev => ({ ...prev, compania_id: companiasRes.data[0].id }));
                // Initialize asistentes with all bomberos of first compania
                const bomberosCompania = bomberosRes.data.filter(b => b.compania_id === companiasRes.data[0].id);
                setAsistentes(bomberosCompania.map(b => ({
                    bombero_id: b.id,
                    nombre: b.nombre_completo,
                    presente: false,
                    evaluacion: '',
                    observacion: '',
                })));
            }
            
            if (isEditing) {
                const actaRes = await bomberosActaAPI.getById(id);
                const acta = actaRes.data;
                setForm({
                    tipo: acta.tipo || 'entrenamiento',
                    titulo: acta.titulo || '',
                    descripcion: acta.descripcion || '',
                    fecha: acta.fecha || '',
                    hora_inicio: acta.hora_inicio || '',
                    hora_fin: acta.hora_fin || '',
                    lugar: acta.lugar || '',
                    compania_id: acta.compania_id || '',
                    responsable_id: acta.responsable_id || '',
                    capacitacion_id: acta.capacitacion_id || 'none',
                    firma_responsable: acta.firma_responsable || false,
                    observacion_general: acta.observacion_general || '',
                });
                setAsistentes(acta.asistentes || []);
            }
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        
        // When compania changes, update asistentes list and reset responsable
        if (field === 'compania_id') {
            const bomberosCompania = bomberos.filter(b => b.compania_id === value);
            setAsistentes(bomberosCompania.map(b => ({
                bombero_id: b.id,
                nombre: b.nombre_completo,
                presente: false,
                evaluacion: '',
                observacion: '',
            })));
            // Reset responsable when compania changes
            setForm(prev => ({ ...prev, compania_id: value, responsable_id: '' }));
        }
    };

    const handleAsistenteChange = (bomberoId, field, value) => {
        setAsistentes(prev => prev.map(a => 
            a.bombero_id === bomberoId ? { ...a, [field]: value } : a
        ));
    };

    const toggleAllPresentes = (value) => {
        setAsistentes(prev => prev.map(a => ({ ...a, presente: value })));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!form.titulo.trim()) {
            toast.error('El título es requerido');
            return;
        }
        if (!form.compania_id) {
            toast.error('Seleccione una compañía');
            return;
        }
        if (!form.responsable_id) {
            toast.error('Seleccione un responsable');
            return;
        }

        try {
            setSaving(true);
            
            const payload = {
                ...form,
                capacitacion_id: form.capacitacion_id === 'none' ? null : form.capacitacion_id,
                asistentes: asistentes,
                empresa_id: user.empresa_id,
            };

            await bomberosActaAPI.create(payload);
            toast.success('Acta creada correctamente');
            navigate('/bomberos/capacitaciones');
        } catch (err) {
            console.error('Error saving:', err);
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const presentesCount = asistentes.filter(a => a.presente).length;

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/capacitaciones')} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <FileText className="h-6 w-6 text-blue-500" />
                        {isEditing ? 'Editar Acta' : 'Nueva Acta de Actividad'}
                    </h1>
                    <p className="text-muted-foreground">
                        Registro de actividad con control de asistencia
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Información de la Actividad */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Calendar className="h-5 w-5" />
                            Información de la Actividad
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Tipo de Actividad *</Label>
                                <Select
                                    value={form.tipo}
                                    onValueChange={(v) => handleChange('tipo', v)}
                                >
                                    <SelectTrigger data-testid="select-tipo">
                                        <SelectValue placeholder="Seleccionar tipo" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {TIPOS_ACTA.map(t => (
                                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Capacitación Asociada (opcional)</Label>
                                <Select
                                    value={form.capacitacion_id}
                                    onValueChange={(v) => handleChange('capacitacion_id', v)}
                                >
                                    <SelectTrigger data-testid="select-capacitacion">
                                        <SelectValue placeholder="Seleccionar" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Sin capacitación asociada</SelectItem>
                                        {capacitaciones.map(c => (
                                            <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="titulo">Título de la Actividad *</Label>
                            <Input
                                id="titulo"
                                value={form.titulo}
                                onChange={(e) => handleChange('titulo', e.target.value)}
                                placeholder="Ej: Entrenamiento de Rescate Vehicular"
                                required
                                data-testid="input-titulo"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="descripcion">Descripción</Label>
                            <Textarea
                                id="descripcion"
                                value={form.descripcion}
                                onChange={(e) => handleChange('descripcion', e.target.value)}
                                placeholder="Descripción de la actividad realizada..."
                                rows={2}
                                data-testid="input-descripcion"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Programación */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Clock className="h-5 w-5" />
                            Fecha, Hora y Lugar
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="fecha">Fecha *</Label>
                                <Input
                                    id="fecha"
                                    type="date"
                                    value={form.fecha}
                                    onChange={(e) => handleChange('fecha', e.target.value)}
                                    required
                                    data-testid="input-fecha"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="hora_inicio">Hora Inicio *</Label>
                                <Input
                                    id="hora_inicio"
                                    type="time"
                                    value={form.hora_inicio}
                                    onChange={(e) => handleChange('hora_inicio', e.target.value)}
                                    required
                                    data-testid="input-hora-inicio"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="hora_fin">Hora Término *</Label>
                                <Input
                                    id="hora_fin"
                                    type="time"
                                    value={form.hora_fin}
                                    onChange={(e) => handleChange('hora_fin', e.target.value)}
                                    required
                                    data-testid="input-hora-fin"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="lugar">Lugar *</Label>
                                <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <Input
                                        id="lugar"
                                        value={form.lugar}
                                        onChange={(e) => handleChange('lugar', e.target.value)}
                                        placeholder="Ubicación de la actividad"
                                        required
                                        data-testid="input-lugar"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Compañía *</Label>
                                <Select
                                    value={form.compania_id}
                                    onValueChange={(v) => handleChange('compania_id', v)}
                                >
                                    <SelectTrigger data-testid="select-compania">
                                        <SelectValue placeholder="Seleccionar compañía" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {companias.map(c => (
                                            <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Responsable / Instructor *</Label>
                                <Select
                                    value={form.responsable_id}
                                    onValueChange={(v) => handleChange('responsable_id', v)}
                                    disabled={!form.compania_id}
                                >
                                    <SelectTrigger data-testid="select-responsable">
                                        <SelectValue placeholder={form.compania_id ? "Seleccionar responsable" : "Seleccione primero una compañía"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {bomberos
                                            .filter(b => b.compania_id === form.compania_id)
                                            .map(b => (
                                                <SelectItem key={b.id} value={b.id}>{b.nombre_completo} - {b.grado}</SelectItem>
                                            ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Control de Asistencia */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="flex items-center gap-2">
                                    <Users className="h-5 w-5" />
                                    Control de Asistencia
                                </CardTitle>
                                <CardDescription>
                                    {presentesCount} de {asistentes.length} presentes
                                </CardDescription>
                            </div>
                            <div className="flex gap-2">
                                <Button type="button" variant="outline" size="sm" onClick={() => toggleAllPresentes(true)}>
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Marcar Todos
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={() => toggleAllPresentes(false)}>
                                    Desmarcar Todos
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {asistentes.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                <p>Seleccione una compañía para cargar el personal</p>
                            </div>
                        ) : (
                            <>
                                <div className="relative mb-4">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Buscar bombero por nombre..."
                                        value={searchAsistente}
                                        onChange={(e) => setSearchAsistente(e.target.value)}
                                        className="pl-10 text-sm"
                                        data-testid="search-asistente-acta"
                                    />
                                </div>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-12">Presente</TableHead>
                                            <TableHead>Bombero</TableHead>
                                            <TableHead className="w-32">Evaluación</TableHead>
                                            <TableHead>Observación</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {asistentes
                                            .filter(a => {
                                                if (!searchAsistente) return true;
                                                return a.nombre?.toLowerCase().includes(searchAsistente.toLowerCase());
                                            })
                                            .map((asistente) => (
                                            <TableRow key={asistente.bombero_id}>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={asistente.presente}
                                                        onCheckedChange={(checked) => handleAsistenteChange(asistente.bombero_id, 'presente', checked)}
                                                        data-testid={`check-${asistente.bombero_id}`}
                                                    />
                                                </TableCell>
                                                <TableCell className="font-medium">{asistente.nombre}</TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={asistente.evaluacion || 'none'}
                                                        onValueChange={(v) => handleAsistenteChange(asistente.bombero_id, 'evaluacion', v === 'none' ? '' : v)}
                                                        disabled={!asistente.presente}
                                                    >
                                                        <SelectTrigger className="w-full">
                                                            <SelectValue placeholder="-" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="none">-</SelectItem>
                                                            <SelectItem value="excelente">Excelente</SelectItem>
                                                            <SelectItem value="bueno">Bueno</SelectItem>
                                                            <SelectItem value="regular">Regular</SelectItem>
                                                            <SelectItem value="deficiente">Deficiente</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        value={asistente.observacion || ''}
                                                        onChange={(e) => handleAsistenteChange(asistente.bombero_id, 'observacion', e.target.value)}
                                                        placeholder="Observaciones"
                                                        className="w-full"
                                                        disabled={!asistente.presente}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </>
                        )}}
                    </CardContent>
                </Card>

                {/* Observaciones y Firma */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle>Observaciones Generales</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <Textarea
                            value={form.observacion_general}
                            onChange={(e) => handleChange('observacion_general', e.target.value)}
                            placeholder="Observaciones generales de la actividad..."
                            rows={3}
                            data-testid="input-observacion-general"
                        />
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="firma"
                                checked={form.firma_responsable}
                                onCheckedChange={(checked) => handleChange('firma_responsable', checked)}
                            />
                            <Label htmlFor="firma" className="cursor-pointer">
                                Confirmo que la información registrada es correcta (Firma del Responsable)
                            </Label>
                        </div>
                    </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex justify-end gap-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate('/bomberos/capacitaciones')}
                    >
                        Cancelar
                    </Button>
                    <Button type="submit" disabled={saving} data-testid="save-btn">
                        {saving ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-2" />
                                Guardar Acta
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </div>
        </>
    );
}
